import cn from 'classnames';
import React, { ChangeEvent, FC, Fragment, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import SignatureCanvas from 'react-signature-canvas';
import { useTranslation } from 'react-i18next';

import {
  Button,
  FormLabel,
  IconAddDoc,
  Input,
  RadioButton,
  Tabs,
} from '@infologistics/frontend-libraries';

import { imageSizeLimitBytes } from '@const/consts';
import { displayBasicNotification } from '@utils/utils';
import { getBase64 } from '@views/profile/utils';

import styles from './EnvelopeMassActionModal.module.scss';

interface IProps {
  disabled: boolean;
  fullName: string;
  onFullNameChange: (fullName: string) => void;
  onSignatureChange: (
    fullSignature: string | null,
    trimmedSignature: string | null,
    source: GraphicalSignatureSource
  ) => void;
  profileSignature: string | null;
  signature: string | null;
  signatureFull: string | null;
  signatureSource: GraphicalSignatureSource;
}

export enum GraphicalSignatureSource {
  PROFILE = 'choose',
  DRAW = 'draw',
  UPLOAD = 'upload',
}

const getSignatureBase64 = (canvas: HTMLCanvasElement): string =>
  canvas.toDataURL('image/png').split(';base64,')[1];

const GraphicalSignatureForm: FC<IProps> = ({
  disabled,
  fullName,
  onFullNameChange,
  onSignatureChange,
  profileSignature,
  signature,
  signatureFull,
  signatureSource,
}) => {
  const { t } = useTranslation(['profile', 'notification']);
  const signatureCanvasRef = useRef<SignatureCanvas>(null);
  const [activeTab, setActiveTab] = useState<GraphicalSignatureSource>(signatureSource);

  useEffect(() => {
    if (
      activeTab !== GraphicalSignatureSource.DRAW ||
      signatureSource !== GraphicalSignatureSource.DRAW ||
      !signatureFull ||
      !signatureCanvasRef.current
    ) return;

    signatureCanvasRef.current.fromDataURL(`data:image/png;base64,${signatureFull}`);
  }, [activeTab, signatureFull, signatureSource]);

  const handleSignatureDrawEnd = (): void => {
    if (disabled || !signatureCanvasRef.current) return;

    onSignatureChange(
      getSignatureBase64(signatureCanvasRef.current.getCanvas()),
      getSignatureBase64(signatureCanvasRef.current.getTrimmedCanvas()),
      GraphicalSignatureSource.DRAW
    );
  };

  const handleClear = (): void => {
    if (disabled) return;

    signatureCanvasRef.current?.clear();
    onSignatureChange(null, null, GraphicalSignatureSource.DRAW);
  };

  const handleTabClick = (tab: string) => (evt: MouseEvent): void => {
    evt.preventDefault();
    if (disabled) return;

    const nextTab = tab as GraphicalSignatureSource;

    setActiveTab(nextTab);
    if (nextTab === GraphicalSignatureSource.PROFILE) {
      onSignatureChange(profileSignature, profileSignature, nextTab);
    } else {
      signatureCanvasRef.current?.clear();
      onSignatureChange(null, null, nextTab);
    }
  };

  const renderProfileSignature = (): ReactNode => {
    if (!profileSignature) return null;

    const handleSelect = (): void => {
      if (disabled) return;

      onSignatureChange(
        profileSignature,
        profileSignature,
        GraphicalSignatureSource.PROFILE
      );
    };

    return (
      <ul className='list-unstyled full-width mt-4'>
        <li className='px-4 py-2 d-flex'>
          <RadioButton
            checked={signatureSource === GraphicalSignatureSource.PROFILE}
            className='mr-4'
            disabled={disabled}
            name='mass_graphical_profile_signature'
            onChange={handleSelect}
            value={profileSignature}
          />
          <img
            alt={t('profile:signature.signatureImage')}
            className='pointer'
            height={54}
            onClick={handleSelect}
            src={`data:image/png;base64, ${profileSignature}`}
          />
        </li>
      </ul>
    );
  };

  const renderSignatureCanvas = (): ReactNode => (
    <div className={styles.graphicalSignatureCanvasWrapper}>
      <SignatureCanvas
        ref={signatureCanvasRef}
        canvasProps={{
          className: styles.graphicalSignatureCanvas,
          height: 200,
          width: 680,
        }}
        clearOnResize={false}
        penColor='#3276B1'
        onEnd={handleSignatureDrawEnd}
      />
      <p className='text-center text-muted mt-2 mb-0'>
        {t('profile:signature.drawDigitalSignature')}
      </p>
      <Button
        classes={styles.graphicalSignatureClear}
        isDisabled={disabled}
        linkStyle='none'
        onClick={handleClear}
        theme='link'
      >
        {t('profile:signature.clear')}
      </Button>
    </div>
  );

  const uploadFiles = (files: File[]): void => {
    if (disabled) return;

    if (files.length > 1) {
      displayBasicNotification({
        title: t('notification:errorTitle'),
        content: t('notification:signature:onlyOneFile'),
        type: 'error',
      });
      return;
    }

    const file = files[0];

    if (!file) return;

    if (!/\.png$/i.test(file.name)) {
      displayBasicNotification({
        title: t('notification:errorTitle'),
        content: t('notification:signature:invalidFileFormat'),
        type: 'error',
      });
      return;
    }

    if (file.size > imageSizeLimitBytes) {
      displayBasicNotification({
        title: t('notification:errorTitle'),
        content: t('notification:signature:imageSizeLimit'),
        type: 'error',
      });
      return;
    }

    void getBase64(file).then((image) => {
      const uploadedSignature = image.split(';base64,')[1];

      onSignatureChange(
        uploadedSignature,
        uploadedSignature,
        GraphicalSignatureSource.UPLOAD
      );
    });
  };

  const handleFilesUpload = (evt: ChangeEvent<HTMLInputElement>): void => {
    if (evt.target.files?.length) uploadFiles(Array.from(evt.target.files));
  };

  const { getRootProps, getInputProps } = useDropzone({
    noClick: true,
    onDrop: uploadFiles,
  });

  const renderSignatureUpload = (): ReactNode => (
    <div className={cn(
      styles.graphicalSignatureUploadWrapper,
      'px-4 py-2 mt-4 d-flex flex-column full-width'
    )}>
      <div
        {...getRootProps()}
        className={cn(
          styles.graphicalSignatureDropZone,
          'd-flex justify-content-center align-items-center flex-column flex-grow-1 relative pt-5'
        )}
      >
        <input {...getInputProps()} disabled={disabled} />
        {signatureSource !== GraphicalSignatureSource.UPLOAD || !signature ? (
          <Fragment>
            <IconAddDoc
              classes={cn(styles.graphicalSignatureUploadIcon, 'mb-4')}
              color='gray-300'
            />
            <FormLabel
              className='fl-button fl-button-base fl-button-default text-color-important'
              text={t('profile:signature.uploadFile')}
            >
              <input
                accept='image/png'
                className='d-none'
                disabled={disabled}
                onChange={handleFilesUpload}
                type='file'
              />
            </FormLabel>
          </Fragment>
        ) : (
          <Fragment>
            <img
              alt={t('profile:signature.signatureImage')}
              className={styles.graphicalSignatureUploadedImage}
              src={`data:image/png;base64, ${signature}`}
            />
            <div className={cn(
              styles.graphicalSignatureUploadActions,
              'absolute d-flex align-items-baseline at-0'
            )}>
              <FormLabel className='fl-button fl-button-base fl-button-link text-primary mr-4'>
                <span className='fl-button_text'>
                  {t('profile:signature.uploadNewFile')}
                </span>
                <input
                  accept='image/png'
                  className='d-none'
                  disabled={disabled}
                  onChange={handleFilesUpload}
                  type='file'
                />
              </FormLabel>
              <Button
                isDisabled={disabled}
                linkStyle='none'
                onClick={() => onSignatureChange(null, null, GraphicalSignatureSource.UPLOAD)}
                theme='link'
              >
                {t('profile:signature.delete')}
              </Button>
            </div>
          </Fragment>
        )}
      </div>
      <p className='text-center text-muted mt-2 mb-0'>
        {t('profile:signature.formatFile')}
      </p>
    </div>
  );

  const tabs = [
    ...(profileSignature ? [{
      name: GraphicalSignatureSource.PROFILE,
      onClick: handleTabClick,
      translation: t('profile:signature.choose'),
    }] : []),
    {
      name: GraphicalSignatureSource.DRAW,
      onClick: handleTabClick,
      translation: t('profile:signature.draw'),
    },
    {
      name: GraphicalSignatureSource.UPLOAD,
      onClick: handleTabClick,
      translation: t('profile:signature.upload'),
    },
  ];

  return (
    <div className={styles.graphicalSignatureForm}>
      <div>
        <FormLabel classes='font-xs' htmlFor='mass_graphical_signer_full_name'>
          {t('profile:fieldLabel.fullName')} <span className='text-danger'>*</span>
        </FormLabel>
        <Input
          disabled={disabled}
          id='mass_graphical_signer_full_name'
          maxLength={100}
          name='mass_graphical_signer_full_name'
          onChange={(evt) => onFullNameChange(evt.target.value)}
          value={fullName}
        />
      </div>
      <Tabs
        activeTab={activeTab}
        content={{
          [GraphicalSignatureSource.PROFILE]: renderProfileSignature(),
          [GraphicalSignatureSource.DRAW]: renderSignatureCanvas(),
          [GraphicalSignatureSource.UPLOAD]: renderSignatureUpload(),
        }}
        tabs={tabs}
        theme='flat'
      />
    </div>
  );
};

export default GraphicalSignatureForm;
